export type OptionType = "C" | "P";

export interface EquitySymbolIdentity {
  kind: "equity";
  raw: string;
  underlying: string;
  canonical: string;
  channelKey: string;
  aliases: string[];
}

export interface OptionSymbolIdentity {
  kind: "option";
  raw: string;
  underlying: string;
  expiry: string;
  optionType: OptionType;
  strike: number;
  occ: string;
  streamer: string;
  compact: string;
  channelKey: string;
  aliases: string[];
}

export type SymbolIdentity = EquitySymbolIdentity | OptionSymbolIdentity;

const OPTION_ROOT = "[A-Z]{1,6}";
const EXPIRY = "\\d{6}";
const TYPE = "[CP]";
const PADDED_STRIKE = "\\d{8}";
const SHORT_STRIKE = "\\d+(?:\\.\\d+)?";

const OCC_PATTERN = new RegExp(
  `^(${OPTION_ROOT})\\s+(${EXPIRY})(${TYPE})(${PADDED_STRIKE})$`,
);
const STREAMER_PATTERN = new RegExp(
  `^\\.(${OPTION_ROOT})(${EXPIRY})(${TYPE})(${SHORT_STRIKE})$`,
);
const COMPACT_PATTERN = new RegExp(
  `^(${OPTION_ROOT})(${EXPIRY})(${TYPE})(${SHORT_STRIKE})$`,
);

const normalizeRaw = (symbol: string): string => symbol.trim().toUpperCase();

const normalizeStrike = (strike: number): number => {
  const rounded = Math.round(strike * 1000) / 1000;
  return Object.is(rounded, -0) ? 0 : rounded;
};

const strikeToStreamer = (strike: number): string => {
  const fixed = normalizeStrike(strike).toFixed(3);
  return fixed.replace(/\.?0+$/, "");
};

const strikeToOcc = (strike: number): string =>
  Math.round(normalizeStrike(strike) * 1000)
    .toString()
    .padStart(8, "0");

const parseStrike = (strike: string, format: "padded" | "short"): number => {
  const numeric =
    format === "padded" ? Number.parseInt(strike, 10) / 1000 : Number(strike);
  return normalizeStrike(numeric);
};

const unique = (values: Array<string | null | undefined>): string[] => {
  const result = new Set<string>();
  values.forEach((value) => {
    const trimmed = value?.trim();
    if (!trimmed) {
      return;
    }
    result.add(trimmed);
    result.add(trimmed.toUpperCase());
  });
  return Array.from(result);
};

const buildOption = (
  raw: string,
  underlying: string,
  expiry: string,
  optionType: string,
  strike: number,
): OptionSymbolIdentity => {
  const normalizedUnderlying = underlying.toUpperCase();
  const normalizedType = optionType as OptionType;
  const normalizedStrike = normalizeStrike(strike);
  const streamerStrike = strikeToStreamer(normalizedStrike);
  const occ = `${normalizedUnderlying.padEnd(6, " ")}${expiry}${normalizedType}${strikeToOcc(normalizedStrike)}`;
  const compact = `${normalizedUnderlying}${expiry}${normalizedType}${streamerStrike}`;
  const streamer = `.${compact}`;
  const collapsedOcc = occ.replace(/\s+/g, "");

  return {
    kind: "option",
    raw,
    underlying: normalizedUnderlying,
    expiry,
    optionType: normalizedType,
    strike: normalizedStrike,
    occ,
    streamer,
    compact,
    channelKey: compact,
    aliases: unique([
      raw,
      raw.replace(/\s+/g, ""),
      occ,
      collapsedOcc,
      streamer,
      compact,
    ]),
  };
};

export function parseSymbol(rawSymbol: string | null | undefined): SymbolIdentity | null {
  const raw = typeof rawSymbol === "string" ? rawSymbol.trim() : "";
  if (!raw) {
    return null;
  }

  const normalized = normalizeRaw(raw);

  let match = normalized.match(OCC_PATTERN);
  if (match) {
    return buildOption(
      raw,
      match[1],
      match[2],
      match[3],
      parseStrike(match[4], "padded"),
    );
  }

  match = normalized.match(STREAMER_PATTERN);
  if (match) {
    return buildOption(
      raw,
      match[1],
      match[2],
      match[3],
      parseStrike(match[4], "short"),
    );
  }

  match = normalized.match(COMPACT_PATTERN);
  if (match) {
    const strikeRaw = match[4];
    return buildOption(
      raw,
      match[1],
      match[2],
      match[3],
      parseStrike(strikeRaw, strikeRaw.length === 8 ? "padded" : "short"),
    );
  }

  const equity = normalized.replace(/\s+/g, "");
  if (!equity) {
    return null;
  }

  return {
    kind: "equity",
    raw,
    underlying: equity,
    canonical: equity,
    channelKey: equity,
    aliases: unique([raw, equity]),
  };
}

export function parseOptionSymbolIdentity(
  rawSymbol: string | null | undefined,
): OptionSymbolIdentity | null {
  const identity = parseSymbol(rawSymbol);
  return identity?.kind === "option" ? identity : null;
}

export function toOccSymbol(rawSymbol: string): string | null {
  return parseOptionSymbolIdentity(rawSymbol)?.occ ?? null;
}

export function toStreamerSymbol(rawSymbol: string): string | null {
  return parseOptionSymbolIdentity(rawSymbol)?.streamer ?? null;
}

export function toChannelKey(rawSymbol: string): string {
  return parseSymbol(rawSymbol)?.channelKey ?? rawSymbol.trim().toUpperCase();
}

export function getSymbolAliases(rawSymbol: string): string[] {
  return parseSymbol(rawSymbol)?.aliases ?? unique([rawSymbol]);
}

export function symbolsEqual(first: string, second: string): boolean {
  return toChannelKey(first) === toChannelKey(second);
}
